import type { ValuesType } from 'utility-types'

import { objectEntries } from '../Entries'
import { nameFunction } from '../Function'
import { executeOnce } from '../Object'
import { is } from './Is'

/** For very specific cases */
type ComparisonFunction<T> = (a: T, b: T) => number

type SimplerComparisonArgs<T> = [toNumber: (value: T) => number, asc?: boolean]
const createPropertySorter = <T>(...[toNumber, asc = true]: SimplerComparisonArgs<T>): ComparisonFunction<T> => (a, b) =>
    (toNumber(a) - toNumber(b)) * (asc ? 1 : -1)

/** For when prop should be skipped */
type NoComparison = false

type PropComparators<T, E = never> = Exclude<ComparisonFunction<T> | SimplerComparisonArgs<T> | NoComparison, E>
type SortConfiguration<T, E = never> = Readonly<{ [P in keyof T]: PropComparators<T[P], E> }>

/**
 * Creates a function that can be used to sort arrays
 */
export const createSorter = <T>(configuration: SortConfiguration<T>, name: string): (a: T, b: T) => number => {
    const getEntries = executeOnce(() =>
        objectEntries(configuration).filter((pair): pair is [keyof T, ValuesType<SortConfiguration<T, NoComparison>>] => pair[1] !== false)
    )

    return nameFunction((a, b) => {
        let order = 0

        for (const [prop, sorter] of getEntries()) {
            type PropType = (typeof a)[typeof prop]
            let internalSorter: PropComparators<PropType, NoComparison> = sorter

            if (is(internalSorter)) {
                /**
                 * Simple config was used
                 */
                internalSorter = createPropertySorter(...(sorter as SimplerComparisonArgs<PropType>))
            }

            order = internalSorter(a[prop], b[prop])

            if (order !== 0) {
                /** Found a direction, so stop looking */
                break
            }
        }

        return order
    }, name)
}
